import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSiteStore } from '../../store/site.store';
import { usePageReadiness } from '../../hooks/usePageReadiness';
import { IntentType, ReadinessLevel } from '../../types/mode.types';

const PageReadinessView: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  const [intentFilter, setIntentFilter] = useState<string>('ALL');
  const [readinessFilter, setReadinessFilter] = useState<string>('ALL');
  const [searchText, setSearchText] = useState<string>('');

  const { data: pages, isLoading, error } = usePageReadiness(siteId, 0, 100);
  const hasGscConfigured = Boolean(selectedSite?.gscPropertyId);

  const latestDataDate = pages && pages.length > 0
    ? pages.reduce((latest, p) => {
        const d = p.indexingDetails?.lastCrawlTime;
        if (d && (!latest || d > latest)) return d;
        return latest;
      }, null as string | null)
    : null;

  const getReadinessColor = (level: ReadinessLevel): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (level) {
      case 'EXCELLENT':
        return 'success';
      case 'GOOD':
        return 'info';
      case 'NEEDS_WORK':
        return 'warning';
      case 'POOR':
        return 'error';
      default:
        return 'default';
    }
  };

  const getIntentIcon = (intent: IntentType) => {
    const icons: Record<IntentType, string> = {
      INFORMATIONAL: '📚',
      COMMERCIAL: '🛍️',
      TRANSACTIONAL: '💳',
      NAVIGATIONAL: '🧭',
      MIXED: '🔀',
      UNKNOWN: '❓',
    };
    return icons[intent] || '❓';
  };

  const formatScopeScore = (score?: number, maxScore?: number) => {
    if (score == null || maxScore == null) return '—';
    return `${Math.round(score)}/${Math.round(maxScore)}`;
  };

  const filteredPages = pages?.filter((page) => {
    if (intentFilter !== 'ALL' && page.detectedIntent !== intentFilter) return false;
    if (readinessFilter !== 'ALL' && page.overallScore.level !== readinessFilter) return false;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      const matchTitle = (page.title || '').toLowerCase().includes(q);
      const matchUrl = (page.url || '').toLowerCase().includes(q);
      if (!matchTitle && !matchUrl) return false;
    }
    return true;
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load page readiness data. Please try again.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: { xs: 420, sm: 860, md: 1100, lg: 1360 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Header */}
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/early-stage')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Page-Level Readiness
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Analyze and improve individual pages for better search performance
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Total Pages
              </Typography>
              <Typography variant="h3">{pages?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Excellent
              </Typography>
              <Typography variant="h3">
                {pages?.filter(p => p.overallScore.level === 'EXCELLENT').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Needs Work
              </Typography>
              <Typography variant="h3">
                {pages?.filter(p => p.overallScore.level === 'NEEDS_WORK').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Poor
              </Typography>
              <Typography variant="h3">
                {pages?.filter(p => p.overallScore.level === 'POOR').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Filter by Query Intent"
                value={intentFilter}
                onChange={(e) => setIntentFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Query Intents</MenuItem>
                <MenuItem value="INFORMATIONAL">📚 Informational</MenuItem>
                <MenuItem value="COMMERCIAL">🛍️ Commercial</MenuItem>
                <MenuItem value="TRANSACTIONAL">💳 Transactional</MenuItem>
                <MenuItem value="NAVIGATIONAL">🧭 Navigational</MenuItem>
                <MenuItem value="MIXED">🔀 Mixed</MenuItem>
                <MenuItem value="UNKNOWN">❓ Unknown</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Filter by Readiness"
                value={readinessFilter}
                onChange={(e) => setReadinessFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Levels</MenuItem>
                <MenuItem value="EXCELLENT">✅ Excellent</MenuItem>
                <MenuItem value="GOOD">🟢 Good</MenuItem>
                <MenuItem value="NEEDS_WORK">⚠️ Needs Work</MenuItem>
                <MenuItem value="POOR">❌ Poor</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

      {/* Pages Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6">
              Pages ({filteredPages?.length || 0})
            </Typography>
            <Box sx={{ width: { xs: '100%', sm: 300 }, minWidth: { xs: 0, sm: 240 } }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by title or URL..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Box>
          </Box>

          {latestDataDate && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Data as of {new Date(latestDataDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
            </Typography>
          )}
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ tableLayout: 'fixed', minWidth: hasGscConfigured ? 1180 : 1040 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ width: { xs: 320, md: hasGscConfigured ? '28%' : '32%' } }}><strong>Page</strong></TableCell>
                  <TableCell align="center" sx={{ width: { xs: 84, md: '8%' } }}><strong>Query Intent</strong></TableCell>
                  <TableCell align="center" sx={{ width: { xs: 116, md: '11%' } }}><strong>Status</strong></TableCell>
                  {hasGscConfigured && (
                    <TableCell align="center" sx={{ width: { xs: 180, md: '15%' } }}><strong>GSC</strong></TableCell>
                  )}
                  <TableCell align="center" sx={{ width: { xs: 72, md: '6%' } }}><strong>Score</strong></TableCell>
                  <TableCell align="left" sx={{ width: { xs: 260, md: hasGscConfigured ? '24%' : '31%' } }}>
                    <Tooltip title="Per-page readiness scope scores">
                      <strong>Scopes</strong>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ width: { xs: 88, md: '5%' }, px: 1 }}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPages && filteredPages.length > 0 ? (
                  filteredPages.map((page) => (
                    <TableRow key={page.pageId} hover>
                      <TableCell sx={{ pr: 1 }}>
                        <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '100%' }} title={page.title || 'No title'}>
                          {page.title || 'No title'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }} title={page.url}>
                          {page.url}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={page.detectedIntent}>
                          <span style={{ fontSize: '1.5rem' }}>
                            {getIntentIcon(page.detectedIntent)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={page.overallScore.level}
                          size="small"
                          color={getReadinessColor(page.overallScore.level)}
                        />
                      </TableCell>
                      {hasGscConfigured && (
                        <TableCell align="left">
                          <Box display="grid" gridTemplateColumns="auto 1fr" columnGap={1} rowGap={0.25} alignItems="center">
                            {page.isIndexed ? <CheckCircleIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                            <Typography variant="caption" color="text.secondary">
                              {page.isIndexed ? 'Indexed' : 'Not indexed'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Clicks</Typography>
                            <Typography variant="caption" fontWeight={600}>{page.clicks.toLocaleString()}</Typography>
                            <Typography variant="caption" color="text.secondary">Impr.</Typography>
                            <Typography variant="caption" fontWeight={600}>{page.impressions.toLocaleString()}</Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="medium">
                          {page.overallScore.score}%
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(page.scopes || []).map((scope) => (
                            <Tooltip key={scope.scopeId} title={`${scope.scopeName}: ${scope.summary}`}>
                              <Chip
                                label={`${scope.scopeId === 'technical_hygiene' ? 'Tech' : scope.scopeName.replace('Core Web Vitals ', 'CWV ').replace('Content Quality & Search Intent', 'Content Intent').replace('Internal Link Graph', 'Links').replace('AI Searchability', 'AI')} ${scope.availabilityStatus === 'not_available' ? 'Unavailable' : formatScopeScore(scope.score, scope.maxScore)}`}
                                size="small"
                                color={scope.availabilityStatus === 'not_available' ? 'default' : getReadinessColor(scope.level)}
                                variant="outlined"
                                sx={{ fontSize: '0.68rem', height: 22, maxWidth: 120 }}
                              />
                            </Tooltip>
                          ))}
                          {(!page.scopes || page.scopes.length === 0) && (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ px: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/early-stage/pages/${page.pageId}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={hasGscConfigured ? 7 : 6} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No pages found matching filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
    </Box>
  );
};

export default PageReadinessView;
